import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Lock } from 'lucide-react';
import { toolIcon } from '../lib/toolIcons';
import ExpenseFormModal from './ExpenseFormModal';
import MilestoneFormModal from './MilestoneFormModal';
import { useExpensesStore } from '../hooks/useExpensesStore';
import { useMilestoneEventsStore } from '../hooks/useMilestoneEventsStore';
import { useSubscription } from '../hooks/useSubscription';

const STORAGE_KEY_SEEN = 'buildpilot.quickAddFab.seen';

/**
 * QuickAddFab, central "+" floating action button (mobile-app-style).
 *
 * Verbatim port of iOS `app/(tabs)/_layout.tsx:32` QUICK_ACTIONS:
 *   1. Add Milestone     → /workspace/tools/milestones?action=add
 *   2. Track Cost        → opens ExpenseFormModal inline
 *   3. Checklist         → /workspace/tools/checklists
 *   4. Scan Defect       → /workspace/tools/defects-scanner?action=scan (premium)
 *   5. Get Advice        → /workspace/advice-chat (premium)
 *   6. Project Health    → /workspace/tools/intelligence (premium)
 *
 * Premium items show a small lock badge when the user isn't on Full
 * Throttle, and route into the paywall instead.
 */
const QuickAddFab = () => {
  const navigate = useNavigate();
  const expenses = useExpensesStore();
  // Pass `undefined` for buildType so the store doesn't filter, we
  // just need the `addMilestone` function here.
  const milestones = useMilestoneEventsStore();
  const { isPremium } = useSubscription();

  const [open, setOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [seen, setSeen] = useState(true);

  // Allow the desktop sidebar's "Quick add" button to open the same sheet
  useEffect(() => {
    const onExternalOpen = () => setOpen(true);
    window.addEventListener('bp:open-quickadd', onExternalOpen);
    return () => window.removeEventListener('bp:open-quickadd', onExternalOpen);
  }, []);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY_SEEN);
      if (!v) setSeen(false);
    } catch { /* no-op */ }
  }, []);

  const markSeen = () => {
    if (seen) return;
    setSeen(true);
    try { localStorage.setItem(STORAGE_KEY_SEEN, '1'); } catch { /* no-op */ }
  };

  // Lock body scroll while the sheet is open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const closeSheet = () => setOpen(false);

  const go = (route) => { closeSheet(); navigate(route); };
  const goPremium = (route) => {
    closeSheet();
    if (!isPremium) navigate('/workspace/paywall');
    else navigate(route);
  };

  const actions = [
    {
      key: 'milestone',
      icon: toolIcon('milestones'),
      title: 'Add Milestone',
      desc: 'Mark a build stage as complete.',
      // Open the inline MilestoneFormModal, same UX pattern as
      // Track Cost, so the user doesn't get yanked out of the
      // dashboard / current tool just to log a milestone.
      onClick: () => { closeSheet(); setMilestoneOpen(true); },
    },
    {
      key: 'cost',
      icon: toolIcon('cost-tracker'),
      title: 'Track Cost',
      desc: 'Log a payment, deposit or variation.',
      onClick: () => { closeSheet(); setExpenseOpen(true); },
    },
    {
      key: 'checklist',
      icon: toolIcon('checklists'),
      title: 'Checklist',
      desc: 'Tick off your next build task.',
      onClick: () => go('/workspace/tools/checklists'),
    },
    {
      key: 'defect',
      icon: toolIcon('defects'),
      title: 'Scan Defect',
      desc: 'AI-powered defect detection.',
      premium: true,
      onClick: () => goPremium('/workspace/tools/defects-scanner?action=scan'),
    },
    {
      key: 'advice',
      icon: toolIcon('advice'),
      title: 'Get Advice',
      desc: 'Ask BuildPilot Intelligence.',
      premium: true,
      onClick: () => goPremium('/workspace/advice-chat'),
    },
    {
      key: 'project-health',
      icon: toolIcon('project-health'),
      title: 'Project Health',
      desc: 'Generate a build health report.',
      premium: true,
      onClick: () => goPremium('/workspace/tools/intelligence'),
    },
  ];

  return (
    <>
      <button
        type="button"
        className={`bp-fab ${open ? 'is-open' : ''} ${!seen && !open ? 'is-pulsing' : ''}`}
        onClick={() => {
          markSeen();
          setOpen((v) => !v);
        }}
        aria-label={open ? 'Close quick add menu' : 'Open quick add menu'}
        aria-expanded={open}
        data-testid="quick-add-fab"
      >
        {!seen && !open && (
          <span className="bp-fab-pulse-ring" aria-hidden="true" />
        )}
        <span className="bp-fab-icon">
          <Plus size={24} strokeWidth={2.4} />
        </span>
      </button>

      {open && (
        <div
          className="bp-fab-backdrop"
          onClick={closeSheet}
          role="presentation"
          data-testid="quick-add-backdrop"
        />
      )}

      <div
        className={`bp-fab-sheet ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label="Quick Actions"
        data-testid="quick-add-sheet"
      >
        <h3 className="bp-fab-sheet-title">Quick Actions</h3>
        <div className="bp-fab-sheet-grid">
          {actions.map((a) => {
            const isLocked = a.premium && !isPremium;
            return (
              <button
                key={a.key}
                type="button"
                className={`bp-fab-action-tile ${isLocked ? 'is-locked' : ''}`}
                onClick={a.onClick}
                data-testid={`quick-add-${a.key}`}
              >
                <span className="bp-fab-action-tile-iconwrap">
                  <img src={a.icon} alt="" className="bp-fab-action-tile-icon" loading="lazy" />
                  {isLocked && (
                    <span className="bp-fab-action-tile-lock" aria-label="Premium">
                      <Lock size={10} color="#fff" strokeWidth={2.4} />
                    </span>
                  )}
                </span>
                <span className="bp-fab-action-tile-label">{a.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      <ExpenseFormModal
        open={expenseOpen}
        onClose={() => setExpenseOpen(false)}
        onSubmit={(data) => expenses.addExpense(data)}
        categories={expenses.allCategories}
      />
      <MilestoneFormModal
        open={milestoneOpen}
        onClose={() => setMilestoneOpen(false)}
        onSubmit={(data) => milestones.addMilestone(data)}
      />
    </>
  );
};

export default QuickAddFab;
